/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useEffect, useState } from 'react'
import { View, Text } from '@components/atoms'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	TextField,
	IconButton,
	Tooltip,
	Typography,
	Chip,
	Select,
	MenuItem,
} from '@mui/material'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'

import { useAppSelector } from '@redux/index'
import {
	SeasonProductAddType,
	SeasonProductBatchUpdateType,
	SeasonProductType,
	SeasonProductUpdateType,
	ProductType,
	SeasonType,
} from '@schemas'
import { blue, grey } from '@mui/material/colors'
import dayjs from 'dayjs'

interface SeasonProductListProps {
	products: ProductType[]
	seasonProducts: SeasonProductType[]
	isEditMode: boolean
	onUpdateSeasonProduct: (
		updatedSeasonProduct: SeasonProductBatchUpdateType[],
		deletedSeasonProduct: SeasonProductType[],
	) => void
}

export const SeasonProductList: React.FC<SeasonProductListProps> = ({
	products,
	seasonProducts,
	isEditMode,
	onUpdateSeasonProduct,
}) => {
	// redux state
	const selectedSeason: SeasonType = useAppSelector(state => state.season.selectedSeason)

	// state
	const [editableProducts, setEditableProducts] = useState<SeasonProductType[]>([])
	const [deletedProducts, setDeletedProducts] = useState<SeasonProductType[]>([])

	useEffect(() => {
		setEditableProducts(seasonProducts)
		setDeletedProducts([])
	}, [seasonProducts])

	const handleEdit = (id: string, field: keyof SeasonProductType, value: any) => {
		const updatedProducts = editableProducts.map(item =>
			item.productId === id ? { ...item, [field]: value } : item,
		)
		setEditableProducts(updatedProducts)
	}

	const handleAdd = () => {
		const newProduct: SeasonProductType = {
			id: `new-${Date.now()}`,
			seasonId: '',
			productId: '',
			seasonUsd: 0,
			seasonKrw: 0,
			seasonDiscountUsd: 0,
			seasonQuantity: 0,
			seasonActiveYn: 0,
			preorderYn: 0,
			partOrderedAt: '',
			productionStartAt: '',
			deliveryStartAt: '',
			deliveryCompletedAt: '',
			updatedBy: '',
			updatedAt: new Date().toISOString(),
			createdBy: '',
			createdAt: new Date().toISOString(),
			productName: '',
			productCode: '',
			productIndex: 0,
			seasonStartDate: '',
			seasonEndDate: '',
			standardUsd: 0,
			soldUsd: 0,
			discountedUsd: 0,
			soldQuantity: 0,
			productUseYn: 0,
			alwaysAvailableYn: 0,
			notStartYn: 0,
			expiredYn: 0,
			soldOutYn: 0,
			status: 'active',
		}
		setEditableProducts([...editableProducts, newProduct])
	}

	const handleDelete = (productId: string) => {
		const deletedProduct = editableProducts.find(item => item.productId === productId)
		if (deletedProduct) {
			setDeletedProducts([...deletedProducts, deletedProduct])
		}
		setEditableProducts(editableProducts.filter(item => item.productId !== productId))
	}

	const handleSave = () => {
		const batchUpdatePayload: SeasonProductBatchUpdateType[] = editableProducts.map(
			product => {
				if (product.id.startsWith('new-')) {
					return {
						iudFlag: 'I',
						seasonId: product.seasonId,
						productId: product.productId,
						usd: product.seasonUsd,
						discountUsd: product.seasonDiscountUsd,
						quantity: product.seasonQuantity,
						activeYn: product.seasonActiveYn,
					} as SeasonProductAddType
				} else {
					const updatePayload: SeasonProductUpdateType = {
						iudFlag: 'U',
						id: product.id,
						seasonId: product.seasonId,
						productId: product.productId,
						usd: product.seasonUsd,
						quantity: product.seasonQuantity,
						discountUsd: product.seasonDiscountUsd,
						activeYn: product.seasonActiveYn,
						partOrderedAt: product.partOrderedAt
							? dayjs(product.partOrderedAt).format('YYYY-MM-DD')
							: '',
						productionStartAt: product.productionStartAt
							? dayjs(product.productionStartAt).format('YYYY-MM-DD')
							: '',
						deliveryStartAt: product.deliveryStartAt
							? dayjs(product.deliveryStartAt).format('YYYY-MM-DD')
							: '',
						deliveryCompletedAt: product.deliveryCompletedAt
							? dayjs(product.deliveryCompletedAt).format('YYYY-MM-DD')
							: '',
					}

					return updatePayload
				}
			},
		)

		onUpdateSeasonProduct(batchUpdatePayload, deletedProducts)
	}

	const totalSales = editableProducts
		.filter(item => item.seasonActiveYn === 1)
		.reduce((sum, item) => sum + item.soldUsd, 0)

	return (
		<View css={style}>
			<TableContainer
				component={Paper}
				elevation={3}
				className="seasonProductTableContainer"
			>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{isEditMode ? (
								<>
									<TableCell className="headerCell">상품</TableCell>
									<TableCell className="headerCell">시즌단가</TableCell>
									<TableCell className="headerCell">시즌할인가</TableCell>
									<TableCell className="headerCell">수량</TableCell>
									<TableCell className="headerCell">활성화여부</TableCell>
									<TableCell className="headerCell dateColumn">파트 주문일</TableCell>
									<TableCell className="headerCell dateColumn">프로덕션 시작일</TableCell>
									<TableCell className="headerCell dateColumn">배송 시작일</TableCell>
									<TableCell className="headerCell dateColumn">배송 완료일</TableCell>
									<TableCell className="headerCell">Actions</TableCell>
								</>
							) : (
								<>
									<TableCell className="headerCell">제품명</TableCell>
									<TableCell className="headerCell">기준단가</TableCell>
									<TableCell className="headerCell">시즌단가</TableCell>
									<TableCell className="headerCell">시즌할인가</TableCell>
									<TableCell className="headerCell">매출액</TableCell>
									<TableCell className="headerCell">목표수량</TableCell>
									<TableCell className="headerCell">판매수량</TableCell>
									<TableCell className="headerCell">활성화여부</TableCell>
									<TableCell className="headerCell dateColumn">파트 주문일</TableCell>
									<TableCell className="headerCell dateColumn">프로덕션 시작일</TableCell>
									<TableCell className="headerCell dateColumn">배송 시작일</TableCell>
									<TableCell className="headerCell dateColumn">배송 완료일</TableCell>
								</>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{editableProducts.map((product, productIdx) => (
							<TableRow
								key={`${product.productId}-${productIdx}`}
								className={`tableRow ${isEditMode ? 'edit' : ''}`}
							>
								{isEditMode ? (
									<>
										<TableCell className="">
											<Select
												value={product.productId}
												onChange={e =>
													handleEdit(product.productId, 'productId', e.target.value)
												}
												variant="outlined"
												size="small"
												fullWidth
											>
												{products.map((p, index) => (
													<MenuItem key={`${p.id}-${index}-${productIdx}`} value={p.id}>
														{p.name}
													</MenuItem>
												))}
											</Select>
										</TableCell>
										<TableCell className="columnUsd">
											<TextField
												type="number"
												value={product.seasonUsd}
												onChange={e =>
													handleEdit(
														product.productId,
														'seasonUsd',
														Number(e.target.value),
													)
												}
												variant="outlined"
												size="small"
												inputProps={{ min: 0, step: 0.01 }}
											/>
										</TableCell>
										<TableCell className="columnDiscountUsd">
											<TextField
												type="number"
												value={product.seasonDiscountUsd}
												onChange={e =>
													handleEdit(
														product.productId,
														'seasonDiscountUsd',
														Number(e.target.value),
													)
												}
												variant="outlined"
												size="small"
												inputProps={{ min: 0, step: 0.01 }}
											/>
										</TableCell>
										<TableCell className="columnQuantity">
											<TextField
												type="number"
												value={product.seasonQuantity}
												onChange={e =>
													handleEdit(
														product.productId,
														'seasonQuantity',
														Number(e.target.value),
													)
												}
												variant="outlined"
												size="small"
												inputProps={{ min: 0, step: 1 }}
											/>
										</TableCell>
										<TableCell className="columnActiveYn">
											<Select
												value={product.seasonActiveYn}
												onChange={e =>
													handleEdit(
														product.productId,
														'seasonActiveYn',
														Number(e.target.value),
													)
												}
												variant="outlined"
												size="small"
											>
												<MenuItem value={1}>Yes</MenuItem>
												<MenuItem value={0}>No</MenuItem>
											</Select>
										</TableCell>
										<TableCell>
											<TextField
												type="date"
												value={dayjs(product.partOrderedAt).format('YYYY-MM-DD')}
												onChange={e =>
													handleEdit(product.productId, 'partOrderedAt', e.target.value)
												}
												variant="outlined"
												size="small"
												disabled={product.id.startsWith('new-')}
											/>
										</TableCell>
										<TableCell>
											<TextField
												type="date"
												value={dayjs(product.productionStartAt).format('YYYY-MM-DD')}
												onChange={e =>
													handleEdit(
														product.productId,
														'productionStartAt',
														e.target.value,
													)
												}
												variant="outlined"
												size="small"
												disabled={product.id.startsWith('new-')}
											/>
										</TableCell>
										<TableCell>
											<TextField
												type="date"
												value={dayjs(product.deliveryStartAt).format('YYYY-MM-DD')}
												onChange={e =>
													handleEdit(product.productId, 'deliveryStartAt', e.target.value)
												}
												variant="outlined"
												size="small"
												disabled={product.id.startsWith('new-')}
											/>
										</TableCell>
										<TableCell>
											<TextField
												type="date"
												value={dayjs(product.deliveryCompletedAt).format('YYYY-MM-DD')}
												onChange={e =>
													handleEdit(
														product.productId,
														'deliveryCompletedAt',
														e.target.value,
													)
												}
												variant="outlined"
												size="small"
												disabled={product.id.startsWith('new-')}
											/>
										</TableCell>
										<TableCell>
											<Tooltip title="Delete">
												<IconButton
													onClick={() => handleDelete(product.productId)}
													size="small"
													color="error"
												>
													<DeleteIcon />
												</IconButton>
											</Tooltip>
										</TableCell>
									</>
								) : (
									<>
										<TableCell css={{ textAlign: 'left' }}>
											{product.productName}
										</TableCell>
										<TableCell css={{ textAlign: 'left' }}>
											{product.standardUsd.toLocaleString('en-US', {
												style: 'currency',
												currency: 'USD',
											})}
										</TableCell>
										<TableCell>
											{product.seasonUsd.toLocaleString('en-US', {
												style: 'currency',
												currency: 'USD',
											})}
										</TableCell>
										<TableCell className="seasonDiscountUsd">
											{product.seasonDiscountUsd.toLocaleString('en-US', {
												style: 'currency',
												currency: 'USD',
											})}
										</TableCell>
										<TableCell>
											{product.soldUsd.toLocaleString('en-US', {
												style: 'currency',
												currency: 'USD',
											})}
										</TableCell>
										<TableCell className="revenueQuantity">
											{product.seasonQuantity.toLocaleString()}
										</TableCell>
										<TableCell className="revenueQuantity">
											{product.soldQuantity.toLocaleString()}
										</TableCell>
										<TableCell className="activeYnCell">
											<Chip
												label={product.status === 'active' ? 'Active' : 'Inactive'}
												color={product.status === 'active' ? 'success' : 'default'}
												size="small"
											/>
										</TableCell>
										<TableCell className="dateColumn">
											{product.partOrderedAt
												? dayjs(product.partOrderedAt).format('YYYY-MM-DD')
												: '-'}
										</TableCell>
										<TableCell className="dateColumn">
											{product.productionStartAt
												? dayjs(product.productionStartAt).format('YYYY-MM-DD')
												: '-'}
										</TableCell>
										<TableCell className="dateColumn">
											{product.deliveryStartAt
												? dayjs(product.deliveryStartAt).format('YYYY-MM-DD')
												: '-'}
										</TableCell>
										<TableCell className="dateColumn">
											{product.deliveryCompletedAt
												? dayjs(product.deliveryCompletedAt).format('YYYY-MM-DD')
												: '-'}
										</TableCell>
									</>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			{isEditMode && (
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={handleAdd}
					className="addButton"
				>
					시즌 제품 추가
				</Button>
			)}
			<View className="summaryView">
				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						매출액:
					</Text>
					<Text category="h6" className="summaryValue">
						{selectedSeason.paidUsd.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>
				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						VAT:
					</Text>
					<Text category="h6" className="summaryValue">
						{((selectedSeason.paidUsd * 10) / 110).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>
				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						할인금액:
					</Text>
					<Text category="h6" className="summaryValue">
						{selectedSeason.discountedUsd.toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>
				<View className="summaryDataView">
					<Text category="h6" className="summaryLabel">
						영업이익:
					</Text>
					<Text category="h6" className="summaryValue">
						{(
							selectedSeason.paidUsd -
							(selectedSeason.paidUsd * 10) / 110
						).toLocaleString('en-US', {
							style: 'currency',
							currency: 'USD',
						})}
					</Text>
				</View>
			</View>
			{isEditMode && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleSave}
					className="saveButton"
				>
					저장
				</Button>
			)}
		</View>
	)
}

const style = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '20px',
	height: 'fit-content',
	overflow: 'hidden',

	'.seasonProductTableContainer': {
		flex: 1,
		overflow: 'auto',
		height: 'fit-content',
		boxShadow: 'none',

		'.headerCell': {
			position: 'sticky',
			top: 0,
			zIndex: 1,
			padding: '0.3rem 0.5rem',
			backgroundColor: '#f5f5f5 !important',
			fontWeight: 'bold',
			fontSize: '11px',
			textAlign: 'center',
		},

		'.tableRow': {
			textAlign: 'center',

			'.MuiTableCell-body': {
				padding: '0.3rem 1rem',
				fontSize: '11px',
				borderBottom: 0,

				'.MuiOutlinedInput-root': {
					'.MuiInputBase-input:not(.MuiSelect-select)': {
						padding: '0.3rem',
						fontSize: '11px',
					},
					'.MuiSelect-select': {
						paddingTop: '0.3rem',
						paddingBottom: '0.3rem',
						paddingLeft: '0.5rem',
						fontSize: '11px',
						lineHeight: 'normal',
					},

					'.MuiOutlinedInput-notchedOutline': {
						borderColor: grey[200],
					},
				},
			},
			'&:nth-of-type(odd)': {
				backgroundColor: grey[50],
			},
			'&:hover': {
				backgroundColor: grey[100],
			},

			'&.edit': {
				'.MuiTableCell-body': {
					padding: '0 0.1rem',
				},
			},
		},

		'.columnUsd': {
			minWidth: '6rem',
		},
		'.columnDiscountUsd': {
			minWidth: '7rem',
		},
		'.columnQuantity': {
			minWidth: '100px',
		},
		'.columnActiveYn': {
			minWidth: '6rem',
		},
	},

	'.addButton': {
		alignSelf: 'flex-start',
	},
	'.totalRevenue': {
		alignSelf: 'flex-end',
	},
	'.totalRevenueValue': {
		color: '#1976d2',
		fontWeight: 'bold',
	},
	'.saveButton': {
		alignSelf: 'flex-end',
	},

	'.dateColumn': {
		minWidth: '100px',
	},
	'.revenueQuantity': {
		minWidth: '6rem',
		textAlign: 'right',
	},
	'.seasonDiscountUsd': {
		minWidth: '6rem',
		textAlign: 'right',
	},
	'.activeYnCell': {
		minWidth: '6rem',
		textAlign: 'center',
	},

	'.summaryView': {
		paddingTop: '1rem',
		borderTop: '1px solid',
		borderColor: grey[400],

		'.summaryDataView': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			justifySelf: 'end',
			width: '20rem',

			'.summaryLabel': {
				marginTop: '0.3rem',
				marginBottom: '0.3rem',
				alignSelf: 'flex-end',
			},

			'.summaryValue': {
				marginTop: '0.3rem',
				marginBottom: '0.3rem',
				color: blue['A400'],
				fontWeight: 'bold',
			},
		},
	},
})
